import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Form, Row, Col, Alert, Pagination } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { useAuthContext } from '../../contexts/AuthContext';
import { diagnosisService } from '../../services/diagnosisService';
import dateService from '../../services/dateService';

const DiagnosisManagementModal = ({ show, onHide, imageId, userRole, patientId }) => {
  const { user } = useAuthContext();
  const [diagnoses, setDiagnoses] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;
  
  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedDiagnosis, setSelectedDiagnosis] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    comment: '',
    diseaseTypes: [],
    cost: ''
  });

  // Filters state
  const [filters, setFilters] = useState({
    viewMode: 'mine',
    search: '',
    startDate: '',
    endDate: ''
  });

  const viewOptions = [
    { value: 'mine', label: 'My Diagnoses' },
    { value: 'all', label: 'All Diagnoses' }
  ];

  // Load diagnoses when component mounts or filters/pagination changes
  useEffect(() => {
    if (show && imageId) {
      loadDiagnoses();
    }
  }, [show, imageId, filters.viewMode, currentPage]);

  // Load diagnoses based on view mode and pagination
  const loadDiagnoses = async () => {
    setLoading(true);
    setError('');
    try {
      let data;
      if (filters.viewMode === 'mine') {
        const allDiagnoses = await diagnosisService.getDiagnosisByCreator(user.id);
        data = imageId ? allDiagnoses.filter(d => d.imageID === parseInt(imageId)) : allDiagnoses;  
      } else {
        data = await diagnosisService.getAllDiagnoses(currentPage, pageSize);
        if (imageId) {
          data = Array.isArray(data.items) 
            ? data.items.filter(d => d.imageID === parseInt(imageId)) 
            : data.filter(d => d.imageID === parseInt(imageId));  
        }
      }
      
      setDiagnoses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load diagnoses:', error);
      setError('Failed to load diagnoses: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Load disease types for the AsyncSelect
  const loadDiseaseTypes = async (inputValue) => {
    try {
      const data = await diagnosisService.searchDiseaseTypes(inputValue);
      return data.map(type => ({
        value: type.id,
        label: type.name
      }));
    } catch (error) {
      console.error('Failed to load disease types:', error);
      return [];
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.comment?.trim()) {
      setError('Comment is required');
      return;
    }

    if (!formData.diseaseTypes?.length) {
      setError('At least one Disease Type is required');
      return;
    }

    const diseaseTypeIds = formData.diseaseTypes
      .filter(dt => dt && dt.value)
      .map(dt => parseInt(dt.value, 10))
      .filter(id => !isNaN(id));

    const diagnosisDTO = {
      ImageID: imageId,
      Comment: formData.comment.trim(),
      CreatorID: user.id,
      CreatorRole: user.role,
      patientID: patientId,
      DiseaseTypeIDs: diseaseTypeIds,
      Cost: parseFloat(formData.cost) || 0
    };

    try {
      if (selectedDiagnosis) {
        await diagnosisService.updateDiagnosis(selectedDiagnosis.DiagnosisID, diagnosisDTO);
      } else {
        await diagnosisService.createDiagnosis(diagnosisDTO);
      }
      
      await loadDiagnoses();
      handleCloseFormModal();
    } catch (err) {
      console.error('Error saving diagnosis:', err);
      setError('Failed to save diagnosis: ' + err.message);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedDiagnosis?.diagnosisID) return;
    
    try {
      await diagnosisService.deleteDiagnosis(selectedDiagnosis.diagnosisID);
      await loadDiagnoses();
      setShowDeleteModal(false);
      setSelectedDiagnosis(null);
    } catch (error) {
      console.error('Failed to delete diagnosis:', error);
      setError('Failed to delete diagnosis: ' + error.message);
    }
  };

  // Handle edit
  const handleEdit = (diagnosis) => {
    setSelectedDiagnosis(diagnosis);
    setFormData({
      comment: diagnosis.comment || '',  
      diseaseTypes: (diagnosis.diseaseTypeNames || []).map(name => ({
        value: name,
        label: name
      })),
      cost: (diagnosis.cost || 0).toString()  
    });
    setShowFormModal(true);
  };

  // Modal close handlers
  const handleCloseFormModal = () => {
    setShowFormModal(false);
    setSelectedDiagnosis(null);
    setFormData({
      comment: '',
      diseaseTypes: [],
      cost: ''
    });
    setError('');
  };

  // Filter diagnoses based on search term and date range
  const filteredDiagnoses = (diagnoses || []).filter(diagnosis => {
    if (!diagnosis) return false;
    
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch = 
        (diagnosis.comment || '').toLowerCase().includes(searchLower) ||  
        (diagnosis.diseaseTypeNames || []).some(name => 
          (name || '').toLowerCase().includes(searchLower)
        ) ||
        (diagnosis.creatorRole || '').toLowerCase().includes(searchLower);  
      
      if (!matchesSearch) return false;
    }
  
    // Date range filter
    if (filters.startDate || filters.endDate) {
      const diagnosisDate = new Date(diagnosis.createdAt);  
      
      if (filters.startDate) {
        const startDate = new Date(filters.startDate);
        if (diagnosisDate < startDate) return false;
      }
      
      if (filters.endDate) {
        const endDate = new Date(filters.endDate);
        endDate.setHours(23, 59, 59, 999);
        if (diagnosisDate > endDate) return false;
      }
    }
  
    return true;
  });

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Diagnosis Management</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        <Row className="mb-3">
          <Col md={3}>
            <Select
              options={viewOptions}
              value={viewOptions.find(opt => opt.value === filters.viewMode)}
              onChange={(option) => {
                setFilters(prev => ({
                  ...prev,
                  viewMode: option.value
                }));
                setCurrentPage(1);
              }}
              className="mb-2"
            />
          </Col>
          <Col md={3}>
            <Form.Control
              type="text"
              placeholder="Search diagnoses..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                search: e.target.value
              }))}
            />
          </Col>
          <Col md={3}>
            <Form.Control
              type="date"
              placeholder="Start Date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                startDate: e.target.value
              }))}
            />
          </Col>
          <Col md={3}>
            <Form.Control
              type="date"
              placeholder="End Date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                endDate: e.target.value
              }))}
            />
          </Col>
        </Row>

        <div className="mb-3 text-end">
          <Button onClick={() => setShowFormModal(true)}>
            Add New Diagnosis
          </Button>
        </div>

        <Table responsive striped bordered hover>
          <thead>
            <tr>
              <th>Date</th>
              <th>Disease Types</th>
              <th>Comment</th>
              <th>Cost</th>
              <th>Creator Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center">Loading...</td>
              </tr>
            ) : filteredDiagnoses.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">No diagnoses found</td>
              </tr>
            ) : (
              filteredDiagnoses.map(diagnosis => (
                <tr key={diagnosis.diagnosisID}>  
                  <td>{dateService.formatDate(diagnosis.createdAt)}</td>  
                  <td>{diagnosis.diseaseTypeNames?.join(', ') || '-'}</td>
                  <td>{diagnosis.comment || '-'}</td> 
                  <td>${diagnosis.cost?.toFixed(2) || '0.00'}</td> 
                  <td>{diagnosis.creatorRole || '-'}</td>  
                  <td>
                    {(diagnosis.creatorID === parseInt(user.id) || user.role === 'Admin') && ( 
                      <>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => handleEdit(diagnosis)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            setSelectedDiagnosis(diagnosis);
                            setShowDeleteModal(true);
                          }}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        {totalPages > 1 && (
          <Pagination className="justify-content-center">
            <Pagination.First
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            />
            {[...Array(totalPages)].map((_, idx) => (
              <Pagination.Item
                key={idx + 1}
                active={idx + 1 === currentPage}
                onClick={() => setCurrentPage(idx + 1)}
              >
                {idx + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        )}

        {/* Nested Form Modal */}
        <Modal show={showFormModal} onHide={handleCloseFormModal}>
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedDiagnosis ? 'Edit Diagnosis' : 'Add New Diagnosis'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Comment</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.comment}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    comment: e.target.value
                  }))}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Disease Types</Form.Label>
                <AsyncSelect
                  isMulti
                  cacheOptions
                  defaultOptions
                  value={formData.diseaseTypes}
                  loadOptions={loadDiseaseTypes}
                  onChange={(value) => setFormData(prev => ({
                    ...prev,
                    diseaseTypes: value || []
                  }))}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Cost</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={formData.cost}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    cost: e.target.value
                  }))}
                  required
                />
              </Form.Group>

              <Button variant="primary" type="submit">
                {selectedDiagnosis ? 'Update' : 'Save'}
              </Button>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete this diagnosis?
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DiagnosisManagementModal;